import {publishOptimisticFields} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {commitMutation, graphql} from 'react-relay'
import type {Environment} from 'relay-runtime'

import type {
  UnlockLockableInput,
  unlockLockableMutation,
  unlockLockableMutation$data,
  unlockLockableMutation$rawResponse,
} from './__generated__/unlockLockableMutation.graphql'

type IssueMutationProps = {
  environment: Environment
  input: UnlockLockableInput
  onError?: (error: Error) => void
  onCompleted?: (response: unlockLockableMutation$data) => void
}

export function commitUnlockLockableMutation({
  environment,
  input: {lockableId},
  onError,
  onCompleted,
}: IssueMutationProps) {
  const inputHash: UnlockLockableInput = {
    lockableId,
  }

  const optimisticResponse: unlockLockableMutation$rawResponse = {
    unlockLockable: {
      unlockedRecord: {
        __typename: 'Issue',
        __isNode: 'Issue',
        id: lockableId,
        locked: false,
      },
    },
  }

  const optimistic = publishOptimisticFields(lockableId, {locked: false})

  let disposable
  try {
    disposable = commitMutation<unlockLockableMutation>(environment, {
      // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
      mutation: graphql`
        mutation unlockLockableMutation($input: UnlockLockableInput!) @raw_response_type {
          unlockLockable(input: $input) {
            unlockedRecord {
              ... on Issue {
                locked
              }
            }
          }
        }
      `,
      variables: {
        input: inputHash,
      },
      optimisticResponse,
      onError: error => {
        optimistic.failed(error)
        if (onError) onError(error)
      },
      onUnsubscribe: () => optimistic.failed(new Error('Unlocking the conversation was cancelled')),
      onCompleted: response => {
        const unlockedRecord = response.unlockLockable?.unlockedRecord
        const locked = unlockedRecord && 'locked' in unlockedRecord ? unlockedRecord.locked : undefined
        if (locked == null) {
          const error = new Error('Failed to unlock the conversation')
          optimistic.failed(error)
          if (onError) onError(error)
          return
        }
        optimistic.completed({locked})
        if (onCompleted) onCompleted(response)
      },
    })
  } catch (error) {
    optimistic.failed(error instanceof Error ? error : new Error('Failed to unlock the conversation'))
    throw error
  }

  return disposable
}
